const utils = require("../utils");
const _extend = Object.assign;
const getDirectionsRenderer = require("./directionsrenderer");
const getDirectionsRoute = require("./directionsroute");
const DirectionsRouteInformations = require("./directionsrouteinformations");

/**
 * converts string to LatLng object
 * @private
 * @param {String} value
 * @returns {String|google.maps.LatLng|BMap.Point}
 */
function _fromStringToLatLngIfNeeded(value) {
    if (/\s*-?\d+\.\d+\s*,\s*-?\d+\.\d+\s*/.test(value)) {
        return new utils.getLatLng(Number.parseFloat(value.split(",")[1]), Number.parseFloat(value.split(",")[0]));
    }
    return value;
}

/**
 * @private
 * @param value
 * @returns {string}
 */
function _invertLatLngIfNeeded(value) {
    if (
        !Number.isNaN(Number.parseFloat(value.split(",")[0])) &&
        !Number.isNaN(Number.parseFloat(value.split(",")[1]))
    ) {
        return `${value.split(",")[1]},${value.split(",")[0]}`;
    }

    return value;
}

/**
 * Directions interface to handle google or baidu directions
 * @abstract
 * @private
 */
class DirectionsInterface {
    constructor(parent) {
        this.parent = parent;
    }

    /**
     * Abstract method to request map with directions points and options
     * @abstract
     */
    getDirectionsFromOrToStore() {}

    /**
     * Abstract method to return directions with parameters
     * @abstract
     * @param {Object} requestParameters
     * @param {Function} successCallback
     * @param {Function} failCallback
     */
    route(requestParameters, successCallback, failCallback) {}

    /**
     * Abstract method to process request reponses
     * @abstract
     * @param {Object<string, BMap.DrivingRouteResult|BMap.WalkingRouteResult>|Object<string, google.maps.DirectionsResult>} responses direction response from google or baidu
     */
    setDirectionsObjectsWithResponse(responses) {}

    /**
     * Abstract method to return provided or current destination as url
     * @abstract
     * @param {woosmap.maps.utils.LatLng|null} origin route origin
     * @param {woosmap.maps.utils.LatLng|null} destination route destination
     * @param {string|null} travelMode travelMode for Baidu if needed
     * Abstract method to get directions link
     */
    getDirectionsLink(origin = null, destination = null, travelMode = null) {}

    /**
     * Abstract to get available travel modes.
     */
    getAvailableTravelModes() {}
}

/**
 * Baidu implemantation of directions methods
 * @private
 */

/**
 * Baidu implemantation of directions methods
 * @private
 */
class GoogleDirectionsImplementation extends DirectionsInterface {
    /**
     * constructor
     * @param {Object} parent
     */
    constructor(parent) {
        super(parent);

        this.successCallback = null;
        this.failCallback = null;
    }

    /**
     * Get directions from, or to, store with google directions services and set results in directionsRenderers
     */
    getDirectionsFromOrToStore() {
        const originDestination = this.parent.get("originDestination");
        const selectedTravelMode = this.parent.get("selectedTravelMode");
        const from = _fromStringToLatLngIfNeeded(originDestination.origin);
        const to = _fromStringToLatLngIfNeeded(originDestination.destination);

        this.request = {
            origin: from,
            destination: to,
            travelMode: google.maps.TravelMode[selectedTravelMode],
        };

        _extend(this.request, this.parent.googleDirectionsRequestOptions);

        if (!this._googleDirectionsService) {
            this._googleDirectionsService = new google.maps.DirectionsService();
        }

        this._googleDirectionsService.route(this.request, (response, status) => {
            if (status === google.maps.DirectionsStatus.OK) {
                this.setDirectionsObjectsWithResponse(response);
            } else if (this.directionsRequestErrorCallback) {
                this.directionsRequestErrorCallback(response, status);
            } else if (!(typeof console === "undefined" || typeof console.error === "undefined")) {
                console.error("google directions services errors: ", response);
            }
        });
    }

    /**
     * Method to get directions.
     * @param {Object} requestParameters request parameters
     * @param {Function} successCallback callback to trigger when route is successful
     * @param {Function} failCallback callback to trigger in case of failure
     * @returns {null}
     */
    route(requestParameters, successCallback, failCallback) {
        if (
            typeof requestParameters.destination === "undefined" ||
            typeof requestParameters.origin === "undefined" ||
            typeof requestParameters.travelMode === "undefined"
        ) {
            return null;
        }

        if (!this._googleDirectionsService) {
            this._googleDirectionsService = new google.maps.DirectionsService();
        }

        this.successCallback = successCallback;
        this.failCallback = failCallback;

        this._googleDirectionsService.route(requestParameters, (response, status) => {
            if (status === google.maps.DirectionsStatus.OK) {
                this.setDirectionsObjectsWithResponse(response);
            } else if (this.failCallback) {
                this.failCallback(response, status);
            } else if (!(typeof console === "undefined" || typeof console.error === "undefined")) {
                console.error("google directions services errors: ", response);
            }
        });
    }

    /**
     * Creates data to show on google map.
     * @param {Object<string, google.maps.DirectionsResult>} response
     */
    setDirectionsObjectsWithResponse(response) {
        const directionsInformations = [];
        const renderers = [];
        this.parent.cleanMapFromRoutes();

        response.routes.forEach((route, index) => {
            const routeInformations = new DirectionsRouteInformations({
                rendererIndex: index,
                travelMode: this.selectedTravelMode,
                summary: route.summary,
                distance: route.legs[0].distance.text,
                durationLong: route.legs[0].duration.text,
                durationShort: utils.strftime(route.legs[0].duration.value),
            });

            if (route.legs[0].duration_in_traffic) {
                routeInformations.setDurationInTrafficLong(route.legs[0].duration_in_traffic.text);
                routeInformations.setDurationInTrafficShort(utils.strftime(route.legs[0].duration_in_traffic.value));
            }

            directionsInformations.push(routeInformations);

            renderers.push(
                getDirectionsRenderer({
                    map: null,
                    directions: {
                        routes: [route],
                        status: response.status,
                        request: response.request,
                    },
                    routeIndex: index,
                    options: this.directionRendererOptions,
                    panel: null,
                }),
            );
        });
        if (this.successCallback) {
            this.successCallback(renderers);
        } else {
            this.parent.set("directionsSummaries", directionsInformations);
            this.parent.set("directionsRenderers", renderers);
            this.parent.set("directionsLink", this.getDirectionsLink());
        }
    }

    /**
     * Returns provided current destination as url.
     * @param {woosmap.maps.utils.LatLng|null} origin route origin
     * @param {woosmap.maps.utils.LatLng|null} destination route destination
     * @param {string|null} travelMode not used with google, required for interface signature
     * @returns {null|String}
     */
    getDirectionsLink(origin = null, destination = null, travelMode = null) {
        let originStr = null;
        let destinationStr = null;
        try {
            if (typeof origin.lat !== "undefined" && typeof origin.lng !== "undefined") {
                if (typeof origin.lat === "function" && typeof origin.lng === "function") {
                    originStr = `${origin.lat()},${origin.lng()}`;
                } else {
                    originStr = `${origin.lat},${origin.lng}`;
                }
            }

            if (typeof destination.lat !== "undefined" && typeof destination.lng !== "undefined") {
                if (typeof destination.lat === "function" && typeof destination.lng === "function") {
                    destinationStr = `${destination.lat()},${destination.lng()}`;
                } else {
                    destinationStr = `${destination.lat},${destination.lng}`;
                }
            }

            return `https://www.google.com/maps/dir/${origin}/${destination}`;
        } catch (e) {
            originStr = null;
            destinationStr = null;
        }

        if (originStr !== null && destinationStr !== null) {
            return `https://www.google.com/maps/dir/${_invertLatLngIfNeeded(origin)}/${_invertLatLngIfNeeded(destination)}`;
        }

        const originDestination = this.parent.get("originDestination");
        if (originDestination?.origin && originDestination.destination) {
            return `https://www.google.com/maps/dir/${_invertLatLngIfNeeded(originDestination.origin)}/${_invertLatLngIfNeeded(originDestination.destination)}`;
        }

        return null;
    }

    /**
     * Returns available travel modes.
     * @returns {string[]}
     */
    getAvailableTravelModes() {
        return [
            google.maps.TravelMode.DRIVING,
            google.maps.TravelMode.TRANSIT,
            google.maps.TravelMode.WALKING,
            google.maps.TravelMode.BICYCLING,
        ];
    }
}

/**
 * @private
 * @param parent
 * @returns {DirectionsInterface}
 */
function getDirectionsImplementation(parent) {
    return new GoogleDirectionsImplementation(parent);
}

module.exports = getDirectionsImplementation;
